import { Component, type ErrorInfo, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { hasNewBuild } from "../../lib/buildVersion";
import { reloadOnceForNewBuild } from "../../lib/lazyWithRetry";
import { toUserFacingError } from "../../../shared/user-facing-error";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  /** 렌더 에러 직후 "새 빌드가 나갔나" 묻는 동안 true — 이 사이엔 오류 문구 대신 중립 안내만 보인다. */
  probing: boolean;
}

// 새 빌드 확인이 이보다 오래 걸리면 그냥 오류 화면을 보여 준다(네트워크가 막힌 상황에서 무한 대기 방지).
const PROBE_TIMEOUT_MS = 2500;

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, probing: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error, probing: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary]", error.message, info.componentStack);
    // ErrorBoundary 가 가로챈 렌더 에러는 uncaught 가 아니라서 Sentry 자동 수집
    // 그물에 안 걸린다 — 직접 전송해야 Sentry 이벤트→Slack 알림까지 이어진다.
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack } },
    });
    void this.reloadIfNewBuild();
  }

  /**
   * 렌더 에러의 흔한 원인 하나는 코드 버그가 아니라 **배포 타이밍**이다 — 배포 전부터 열려 있던
   * 탭(옛 화면 코드)이 새 서버의 달라진 응답을 받아 터진다(2026-09-03 구간표 업로드,
   * `undefined.length`). 그 경우 오류 화면의 「다시 시도」는 같은 옛 코드를 다시 그릴 뿐 소용이
   * 없고, 새로고침만이 답이다. 그래서 새 빌드가 나갔으면 오류 화면 대신 조용히 한 번 새로고침한다.
   * 가드 도장은 lazyWithRetry 와 공유해(reloadOnceForNewBuild) 두 층이 각자 새로고침하지 않는다.
   */
  private async reloadIfNewBuild() {
    const fresh = await Promise.race([
      hasNewBuild(PROBE_TIMEOUT_MS),
      new Promise<boolean>((resolve) => setTimeout(() => resolve(false), PROBE_TIMEOUT_MS)),
    ]);
    if (fresh && reloadOnceForNewBuild()) return; // 곧 페이지가 통째로 새로 뜬다
    this.setState({ probing: false });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      if (this.state.probing) {
        return (
          <div className="flex h-48 items-center justify-center text-sm text-gray-500 dark:text-gray-400">
            화면을 다시 불러오는 중…
          </div>
        );
      }

      return (
        // 원문은 화면에 안 그리고 data-error-detail 속성에만 둔다 — 헤드리스 검증·개발자 도구에서는
        // 읽히고(`[data-error-detail]`), 판매점 직원 눈에는 안 보인다. 콘솔 `[ErrorBoundary]` 에도 찍힌다.
        <div
          className="flex flex-col items-center justify-center h-48 gap-4"
          data-error-boundary=""
          data-error-detail={this.state.error?.message ?? ""}
        >
          <p className="text-red-500 font-medium">
            화면을 표시하다 문제가 생겼어요.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {/* 렌더 에러 원문(`Cannot read properties of undefined…`)은 위 Sentry 로만 보낸다. */}
            {toUserFacingError(
              this.state.error,
              "개발팀에 자동으로 알렸어요. 새로고침하거나 잠시 후 다시 시도해 주세요.",
            )}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => this.setState({ hasError: false, error: null, probing: false })}
              className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 transition-colors"
            >
              다시 시도
            </button>
            <button
              onClick={() => window.location.reload()}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              새로고침
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
